<script>
  import { onMount } from "svelte";
  import { Loading } from "carbon-components-svelte";
  import { TextArea, Button } from "paxray-components-svelte";
  import Header from "lib/components/Header.svelte";
  import { ROUTES } from "@utils/routes.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import SurveyEstimatedTime from "./components/SurveyEstimatedTime.svelte";
  import SurveyStepIntro from "./components/SurveyStepIntro.svelte";
  import {
    taskFeedbackKpis,
    taskFeedbackOccurrences,
    taskFeedbackTimeShares,
    taskFeedbackTopics,
  } from "./taskFeedbackDefinition.js";
  import { CHECK_DARK_ICON, CHECKBOX_ICON, CLOSE_ICON, PLUS_ICON } from '@utils/icons.js';

  const surveyUserId = new URLSearchParams(window.location.search).get(
    "survey_user",
  );
  let loading = true;
  let survey = null;
  let currentTaskIndex = 0;
  let answers = {};
  let error = "";
  let submitting = false;
  let completed = false;

  $: tasks = survey?.config?.tasks || [];
  $: selectedKpis = taskFeedbackKpis.filter((kpi) =>
    survey?.config?.kpis?.includes(kpi.id),
  );
  $: currentTask = tasks[currentTaskIndex];
  $: currentAnswer = currentTask ? answers[currentTask.id] : null;
  $: currentTaskDescription = currentTask
    ? `<strong>${escapeHtml(currentTask.name)}</strong><br><span>${escapeHtml(currentTask.hierarchy.join(" · "))}</span>`
    : "";

  function escapeHtml(value) {
    return String(value).replace(
      /[&<>"']/g,
      (character) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[character],
    );
  }

  function blankIdea() {
    return { text: "", topics: [], timeShare: "", occurrence: "" };
  }

  function blankAnswer() {
    return { skipped: false, values: {}, ideas: [blankIdea()] };
  }

  function updateAnswer(changes) {
    answers = {
      ...answers,
      [currentTask.id]: { ...currentAnswer, ...changes },
    };
    error = "";
  }

  function selectValue(kpiId, value) {
    updateAnswer({ values: { ...currentAnswer.values, [kpiId]: value } });
  }

  function setSkipped(skipped) {
    updateAnswer({ skipped });
  }

  function ideaStarted(idea) {
    return Boolean(
      idea.text.trim() ||
        idea.topics.length ||
        idea.timeShare ||
        idea.occurrence,
    );
  }

  function ideaComplete(idea) {
    return Boolean(
      idea.text.trim() &&
        idea.topics.length &&
        idea.timeShare &&
        idea.occurrence,
    );
  }

  function updateIdea(index, changes) {
    const ideas = currentAnswer.ideas.map((idea, ideaIndex) =>
      ideaIndex === index ? { ...idea, ...changes } : idea,
    );
    updateAnswer({ ideas });
  }

  function toggleTopic(index, topic) {
    const idea = currentAnswer.ideas[index];
    updateIdea(index, {
      topics: idea.topics.includes(topic)
        ? idea.topics.filter((item) => item !== topic)
        : [...idea.topics, topic],
    });
  }

  function toggleIdeaValue(index, field, value) {
    updateIdea(index, {
      [field]: currentAnswer.ideas[index][field] === value ? "" : value,
    });
  }

  function addIdea() {
    const lastIdea = currentAnswer.ideas[currentAnswer.ideas.length - 1];
    if (lastIdea && !ideaComplete(lastIdea)) return;
    updateAnswer({ ideas: [...currentAnswer.ideas, blankIdea()] });
  }

  function removeIdea(index) {
    updateAnswer({
      ideas:
        index === 0
          ? [blankIdea(), ...currentAnswer.ideas.slice(1)]
          : currentAnswer.ideas.filter((_, ideaIndex) => ideaIndex !== index),
    });
  }

  function validateCurrentTask() {
    if (currentAnswer.skipped) return true;
    const missing = selectedKpis.find((kpi) => !currentAnswer.values[kpi.id]);
    if (missing) {
      error = "Please rate every selected aspect—or skip this task.";
      return false;
    }
    if (
      currentAnswer.ideas.some(
        (idea) => ideaStarted(idea) && !ideaComplete(idea),
      )
    ) {
      error =
        "Please complete the improvement idea or clear it before continuing.";
      return false;
    }
    return true;
  }

  async function nextTask() {
    if (!validateCurrentTask()) return;
    if (currentTaskIndex < tasks.length - 1) {
      currentTaskIndex += 1;
      error = "";
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    submitting = true;
    try {
      const response = await fetchWithSession(
        `/api/knextractor/task-feedback-surveys/by-user/${surveyUserId}/responses`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tasks: tasks.map((task) => ({
              task_id: task.id,
              skipped: answers[task.id].skipped,
              values: answers[task.id].values,
              ideas: answers[task.id].ideas,
            })),
          }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not submit the survey.");
      completed = true;
    } catch (submitError) {
      error = submitError.message;
    } finally {
      submitting = false;
    }
  }

  onMount(async () => {
    try {
      const response = await fetchWithSession(
        `/api/knextractor/task-feedback-surveys/by-user/${surveyUserId}`,
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Survey not found.");
      survey = result;
      completed = result.survey_answered;
      answers = Object.fromEntries(
        (result.config?.tasks || []).map((task) => [task.id, blankAnswer()]),
      );
    } catch (loadError) {
      error = loadError.message;
    } finally {
      loading = false;
    }
  });
</script>

<div class="survey_header">
  <Header
    section="Potential Scout"
    projectNameOverride={survey?.name || "Employee task feedback"}
    projectHref={ROUTES.dam.root}
    area="Surveys"
    areaHref={ROUTES.knex.root}
    areaClickable={false}
    breadcrumb="Survey"
  />
</div>

{#if loading}
  <div class="state"><Loading withOverlay={false} /></div>
{:else if completed}
  <main class="maincontainer">
    <div class="canvas">
      <section class="survey_status completion">
        <h2>Thank you for your feedback.</h2>
        <p>Your answers were saved. You can close this window.</p>
      </section>
    </div>
  </main>
{:else if !survey || !currentTask}
  <main class="maincontainer">
    <div class="canvas">
      <section class="survey_status completion error">
        <h2>Survey unavailable</h2>
        <p>{error || "This survey cannot be opened."}</p>
      </section>
    </div>
  </main>
{:else}
  <main class="maincontainer">
    <div class="canvas">
      <section class="survey_status step_3">
        <div class="survey_body">
          <div class="task_meta">
            <span>Task {currentTaskIndex + 1} of {tasks.length}</span
            ><SurveyEstimatedTime
              label="Estimated time"
              value="under 1 minute"
            />
          </div>
          <SurveyStepIntro
            title="Please rate this task"
            description={currentTaskDescription}
            variant="detail"
          />

          <section class="assessment_gate">
            <strong>Do you know this task well enough to assess it?</strong>
            <div class="buttons_wrapper">
              <Button
                text="Yes, I can assess it"
                kind={!currentAnswer.skipped ? "primary" : "outline"}
                on:click={() => setSkipped(false)}
              />
              <Button
                text="Skip this task"
                kind={currentAnswer.skipped ? "secondary" : "outline"}
                on:click={() => setSkipped(true)}
              />
            </div>
          </section>

          {#if !currentAnswer.skipped}
            <div class="questions">
              {#each selectedKpis as kpi}
                <section class="dimension_task">
                  <div class="dimension_task_text">
                    <strong>{kpi.title}</strong>
                    <p>{kpi.question}</p>
                  </div>
                  <div class="buttons_wrapper">
                    {#each kpi.options as option, index}
                      <div
                        class:option
                        class:selected={currentAnswer.values[kpi.id] ===
                          String(index + 1)}
                      >
                        <Button
                          kind="secondary"
                          text={option}
                          icon={currentAnswer.values[kpi.id] ===
                          String(index + 1)
                            ? CHECK_DARK_ICON
                            : CHECKBOX_ICON}
                          iconRight
                          on:click={() =>
                            selectValue(kpi.id, String(index + 1))}
                        />
                      </div>
                    {/each}
                  </div>
                </section>
              {/each}
            </div>

            {#if survey.config.comment_enabled}
              <section class="ideas_section">
                <div class="ideas_heading">
                  <strong>What would you improve? <em>Optional</em></strong>
                  <p>Describe one idea at a time and classify it briefly.</p>
                </div>
                {#each currentAnswer.ideas as idea, index}
                  <article class="improvement_idea_card">
                    <header>
                      <span>{index + 1}</span><strong>Improvement idea</strong
                      ><Button
                        icon={CLOSE_ICON}
                        size="small"
                        kind="secondary"
                        iconDescription="Delete idea"
                        on:click={() => removeIdea(index)}
                      />
                    </header>
                    <div class="improvement_idea_content">
                      <label
                        class="idea_description"
                        for={`idea-description-${index}`}
                        ><strong
                          >Description <em
                            class="required_tag"
                            class:optional={!ideaStarted(idea)}
                            >{ideaStarted(idea) ? "Required" : "Optional"}</em
                          ></strong
                        ><TextArea
                          id={`idea-description-${index}`}
                          light
                          rows={4}
                          labelText=""
                          placeholder="Describe the improvement idea..."
                          value={idea.text}
                          on:input={(event) =>
                            updateIdea(index, {
                              text: event.currentTarget.value,
                            })}
                        /></label
                      >
                      <div class="idea_choices_column">
                        <div class="idea_field">
                          <strong
                            >Related topics <em
                              class="required_tag"
                              class:optional={!ideaStarted(idea)}
                              >{ideaStarted(idea) ? "Required" : "Optional"}</em
                            ></strong
                          >
                          <div class="choice_buttons">
                            {#each taskFeedbackTopics as topic}<button
                                type="button"
                                class:selected={idea.topics.includes(topic)}
                                on:click={() => toggleTopic(index, topic)}
                                >{topic}</button
                              >{/each}
                          </div>
                        </div>
                        <div class="idea_rating_grid">
                          <div class="idea_field">
                            <strong
                              >Impact on task time <em
                                class="required_tag"
                                class:optional={!ideaStarted(idea)}
                                >{ideaStarted(idea)
                                  ? "Required"
                                  : "Optional"}</em
                              ></strong
                            >
                            <div class="choice_buttons">
                              {#each taskFeedbackTimeShares as value}<button
                                  type="button"
                                  class:selected={idea.timeShare === value}
                                  on:click={() =>
                                    toggleIdeaValue(index, "timeShare", value)}
                                  >{value}</button
                                >{/each}
                            </div>
                          </div>
                          <div class="idea_field">
                            <strong
                              >How often does it occur? <em
                                class="required_tag"
                                class:optional={!ideaStarted(idea)}
                                >{ideaStarted(idea)
                                  ? "Required"
                                  : "Optional"}</em
                              ></strong
                            >
                            <div class="choice_buttons">
                              {#each taskFeedbackOccurrences as value}<button
                                  type="button"
                                  class:selected={idea.occurrence === value}
                                  on:click={() =>
                                    toggleIdeaValue(index, "occurrence", value)}
                                  >{value}</button
                                >{/each}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                {/each}
                <div class="add_improvement_button">
                  <Button
                    kind="secondary"
                    text="Add another idea"
                    icon={PLUS_ICON}
                    iconLeft
                    disabled={!ideaComplete(
                      currentAnswer.ideas[currentAnswer.ideas.length - 1],
                    )}
                    on:click={addIdea}
                  />
                </div>
              </section>
            {/if}
          {/if}
          {#if error}<p class="form_error">{error}</p>{/if}
        </div>
        <footer class="survey_footer">
          <div class="step_footer">
            <Button
              text="Back"
              kind="secondary"
              disabled={currentTaskIndex === 0 || submitting}
              on:click={() => {
                currentTaskIndex -= 1;
                error = "";
              }}
            /><Button
              text={submitting
                ? "Submitting..."
                : currentTaskIndex === tasks.length - 1
                  ? "Submit feedback"
                  : "Next task"}
              kind="primary"
              disabled={submitting}
              on:click={nextTask}
            />
          </div>
        </footer>
      </section>
    </div>
  </main>
{/if}

<style>
  .state {
    min-height: 70vh;
    display: grid;
    place-items: center;
  }
  .maincontainer {
    min-height: calc(100vh - 5rem);
    padding: 1rem 2rem;
    box-sizing: border-box;
  }
  .canvas {
    width: min(68rem, 100%);
    margin: 0 auto;
  }
  .survey_status {
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
  }
  .task_meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;
    color: #59616c;
    font-size: 0.85rem;
  }
  .assessment_gate {
    margin: 0 0 1.75rem;
  }
  .assessment_gate > strong,
  .dimension_task_text strong,
  .ideas_heading > strong {
    font-size: 0.9rem;
  }
  .assessment_gate .buttons_wrapper {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.65rem;
  }
  .questions {
    display: grid;
    gap: 1.25rem;
  }
  .dimension_task {
    display: grid;
    grid-template-columns: minmax(9rem, 1.4fr) minmax(30rem, 4fr);
    align-items: center;
    min-height: 3rem;
  }
  .dimension_task_text {
    padding-right: 1rem;
    color: #252a31;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .dimension_task_text p,
  .ideas_heading p {
    margin: 0.3rem 0 0;
    color: #59616c;
    font-size: 0.8rem;
    font-weight: 400;
    line-height: 1.4;
  }
  .dimension_task .buttons_wrapper {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.8rem;
    height: 100%;
  }
  .option :global(.mainbutton) {
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 3rem;
    padding: 0.65rem 1rem !important;
    border: 1px solid #dfe3e8 !important;
    border-radius: 0.5rem;
    background: #fff !important;
    color: var(--pax-black) !important;
  }
  .option :global(.mainbutton .button_text) {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .option.selected :global(.mainbutton) {
    border-color: var(--pax-black) !important;
    background: #fff !important;
    color: var(--pax-black) !important;
  }
  .option :global(.mainbutton .icon) {
    min-width: 1rem;
    flex: 0 0 auto;
  }
  .option.selected :global(.mainbutton .icon),
  .option.selected :global(.mainbutton .icon img) {
    width: 1rem;
    height: 1rem;
  }
  .ideas_section {
    margin-top: 2rem;
  }
  .ideas_heading em {
    margin-left: 0.35rem;
    color: #98a2b3;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
  }
  .improvement_idea_card {
    width: 100%;
    box-sizing: border-box;
    margin-top: 1rem;
    padding: 1rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.65rem;
    background: #fff;
  }
  .improvement_idea_card header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }
  .improvement_idea_card header > span {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.45rem;
    background: #f1f3f5;
    font-weight: 700;
  }
  .improvement_idea_card header > strong {
    font-size: 0.85rem;
  }
  .improvement_idea_card header :global(button) {
    margin-left: auto;
  }
  .improvement_idea_content {
    display: grid;
    grid-template-columns: minmax(15rem, 1.2fr) minmax(18rem, 1.5fr);
    gap: 1.5rem;
    margin-top: 1rem;
  }
  .idea_field,
  .idea_description {
    display: flex;
    flex-direction: column;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    gap: 0.5rem;
    width: 100%;
    margin-top: 0.85rem;
    text-align: left;
  }
  .idea_field strong,
  .idea_description > strong {
    font-size: 0.7rem;
  }
  .required_tag {
    margin-left: 0.35rem;
    padding: 0.25rem 0.55rem;
    border: 1px solid #c97982;
    border-radius: 1rem;
    background: var(--pax-red);
    color: #8f3d46;
    font-size: 0.65rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.1;
  }
  .required_tag.optional {
    border-color: #d6dce5;
    background: #f3f5f7;
    color: #7a8491;
  }
  .idea_choices_column {
    display: grid;
    gap: 1rem;
  }
  .choice_buttons {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    flex-wrap: wrap;
    gap: 0.45rem;
    width: 100%;
  }
  .choice_buttons button {
    max-width: 100%;
    padding: 0.5rem 0.7rem;
    overflow: hidden;
    border: 1px solid #cfd4da;
    border-radius: 0.4rem;
    background: #fff;
    color: var(--pax-black);
    font-size: 0.68rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
  .choice_buttons button.selected {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .idea_rating_grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  .add_improvement_button {
    margin-top: 1rem;
  }
  .add_improvement_button :global(.mainbutton) {
    border: 1px solid #d3dae3;
    background: var(--pax-white);
    color: var(--pax-black);
  }
  .form_error {
    margin: 1rem 0 0;
    color: #b42318;
    font-size: 0.85rem;
  }
  .survey_footer {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e4e6e9;
  }
  .step_footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
  .completion {
    margin-top: 4rem;
    text-align: center;
  }
  .completion h2 {
    margin-top: 0;
  }
  .completion p {
    color: #59616c;
  }
  .completion.error h2 {
    color: #b42318;
  }
  @media (max-width: 800px) {
    .maincontainer {
      padding: 1rem;
    }
    .survey_status {
      padding: 1.5rem;
    }
    .dimension_task,
    .improvement_idea_content {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  @media (max-width: 600px) {
    .task_meta {
      align-items: flex-start;
      flex-direction: column;
      gap: 0.5rem;
    }
    .dimension_task .buttons_wrapper,
    .idea_rating_grid {
      grid-template-columns: 1fr;
    }
    .assessment_gate .buttons_wrapper,
    .step_footer {
      flex-wrap: wrap;
    }
  }
</style>
